/**
 * KoredorTourContext — state, persistence, and controls for the guided
 * Koredor tour. localStorage-backed so dismissal/completion survives
 * reloads. No backend, no mutations to real data.
 *
 * Phase A: now supports three viewing modes — Full (every step), Express
 * (chapter openers + key handoffs), and Role-filtered (only steps tagged
 * for that role). The "playable" indices are derived from
 * `getPlayableIndices(mode)`. `currentIndex` still stores the GLOBAL step
 * index so resume-after-refresh keeps working across mode changes.
 *
 * Navigation policy: route changes are driven from a single effect on
 * `currentIndex`, not inline in click handlers. This kills double-click
 * races (Next clicked twice while a microtask is queued) and lets the
 * tour resume to the correct route after a hard refresh.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  getStepByIndex, TOTAL_STEPS, TOUR_CHAPTERS, getPlayableIndices,
  type TourViewMode, type TourRole,
} from "./tourChapters";


const STORAGE_KEY = "koredor.tour.v2"; // bumped from accounting.v1 for new shape

type Status = "unseen" | "prompted" | "dismissed" | "in-progress" | "completed";

interface Persisted {
  status: Status;
  currentIndex?: number;
  mode?: TourViewMode;
  dismissedAt?: string;
  completedAt?: string;
  dontShowAgain?: boolean;
}

interface TourContextValue {
  status: Status;
  active: boolean;
  currentIndex: number;       // GLOBAL step index
  totalSteps: number;         // total steps in current mode
  positionInMode: number;     // 0-based position within the playable list
  mode: TourViewMode;
  dontShowAgain: boolean;
  start: (fromIndex?: number) => void;
  next: () => void;
  prev: () => void;
  jumpToChapter: (chapterIndex: number) => void;
  setMode: (mode: TourViewMode) => void;
  exit: () => void;
  complete: () => void;
  markPrompted: () => void;
  dismiss: (permanent?: boolean) => void;
  reset: () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

function read(): Persisted {
  if (typeof window === "undefined") return { status: "unseen" };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { status: "unseen" };
    return JSON.parse(raw);
  } catch { return { status: "unseen" }; }
}

function write(p: Persisted) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

const DEFAULT_MODE: TourViewMode = { kind: "full" };

export function AccountingTourProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [state, setState] = useState<Persisted>(() => read());
  // Tracks the last step index we navigated FOR. Guards against re-firing on
  // location.pathname changes (user navigating away should never be overridden).
  const lastNavigatedIndexRef = useRef<number | null>(null);
  // True only when the tour was explicitly started/jumped in this browser session.
  // Prevents a page load/refresh from force-navigating to the tour step when the
  // tour was merely "resumed" from localStorage (in-progress state persisted).
  const startedInSessionRef = useRef(false);

  useEffect(() => { write(state); }, [state]);

  const active = state.status === "in-progress";
  const mode = state.mode ?? DEFAULT_MODE;
  const playable = useMemo(() => {
    const list = getPlayableIndices(mode);
    return list.length > 0 ? list : getPlayableIndices(DEFAULT_MODE);
  }, [mode]);
  const currentIndex = state.currentIndex ?? playable[0] ?? 0;
  const positionInMode = Math.max(0, playable.indexOf(currentIndex));
  const totalSteps = playable.length;

  // Single source of navigation: fire ONLY when currentIndex changes (or the
  // tour becomes active for the first time in this session). Never fires on
  // location.pathname changes (removed in d1337383). Also skips the initial
  // mount when the tour is merely resumed from localStorage — the user should
  // stay where they are; only explicit start/jump/next/prev should navigate.
  useEffect(() => {
    if (!active) { lastNavigatedIndexRef.current = null; return; }
    // Skip if we already navigated for this index (prevents double-navigation).
    if (lastNavigatedIndexRef.current === currentIndex) return;
    // On resume (page load with tour "in-progress" from localStorage): mark the
    // current index without navigating. The user stays on whatever page they're
    // on. Navigation only happens when they explicitly advance the tour.
    if (lastNavigatedIndexRef.current === null && !startedInSessionRef.current) {
      lastNavigatedIndexRef.current = currentIndex;
      return;
    }
    lastNavigatedIndexRef.current = currentIndex;
    const resolved = getStepByIndex(currentIndex);
    if (!resolved?.step.route) return;
    navigate(resolved.step.route);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, currentIndex, navigate]);

  const start = useCallback((fromIndex?: number) => {
    startedInSessionRef.current = true;
    lastNavigatedIndexRef.current = null;
    setState(s => {
      const m = s.mode ?? DEFAULT_MODE;
      const list = getPlayableIndices(m);
      const target = typeof fromIndex === "number" ? fromIndex : (list[0] ?? 0);
      return { ...s, status: "in-progress", currentIndex: target };
    });
  }, []);

  const next = useCallback(() => {
    setState(s => {
      const m = s.mode ?? DEFAULT_MODE;
      const list = getPlayableIndices(m);
      const cur = s.currentIndex ?? list[0] ?? 0;
      const pos = list.indexOf(cur);
      // Find next playable step >= cur+1 (handles cases where cur was somehow
      // not in the playable list after a mode change).
      const nextGlobal = pos === -1
        ? list.find(i => i > cur)
        : list[pos + 1];
      if (nextGlobal === undefined) {
        return { ...s, status: "completed", completedAt: new Date().toISOString() };
      }
      return { ...s, currentIndex: nextGlobal };
    });
  }, []);

  const prev = useCallback(() => {
    setState(s => {
      const m = s.mode ?? DEFAULT_MODE;
      const list = getPlayableIndices(m);
      const cur = s.currentIndex ?? list[0] ?? 0;
      const pos = list.indexOf(cur);
      const prevGlobal = pos <= 0
        ? list[0]
        : list[pos - 1];
      return { ...s, currentIndex: prevGlobal ?? cur };
    });
  }, []);

  const jumpToChapter = useCallback((chapterIndex: number) => {
    startedInSessionRef.current = true;
    let cursor = 0;
    for (let i = 0; i < TOUR_CHAPTERS.length; i++) {
      if (i === chapterIndex) {
        // Snap to the first playable step in this chapter under the current mode.
        const chapterEnd = cursor + TOUR_CHAPTERS[i].steps.length;
        setState(s => {
          const m = s.mode ?? DEFAULT_MODE;
          const list = getPlayableIndices(m);
          const target = list.find(g => g >= cursor && g < chapterEnd) ?? cursor;
          return { ...s, status: "in-progress", currentIndex: target };
        });
        return;
      }
      cursor += TOUR_CHAPTERS[i].steps.length;
    }
  }, []);

  const setMode = useCallback((newMode: TourViewMode) => {
    setState(s => {
      const list = getPlayableIndices(newMode);
      const cur = s.currentIndex ?? 0;
      // Snap currentIndex into the new playable list — pick the nearest
      // playable step >= cur, falling back to the first.
      const snapped = list.find(g => g >= cur) ?? list[0] ?? 0;
      return { ...s, mode: newMode, currentIndex: snapped };
    });
  }, []);

  const exit = useCallback(() => {
    setState(s => ({ ...s, status: s.status === "in-progress" ? "dismissed" : s.status }));
  }, []);

  const complete = useCallback(() => {
    setState(s => ({ ...s, status: "completed", completedAt: new Date().toISOString() }));
  }, []);

  const markPrompted = useCallback(() => {
    setState(s => s.status === "unseen" ? { ...s, status: "prompted" } : s);
  }, []);

  const dismiss = useCallback((permanent = false) => {
    setState(s => ({ ...s, status: "dismissed", dismissedAt: new Date().toISOString(), dontShowAgain: permanent || s.dontShowAgain }));
  }, []);

  const reset = useCallback(() => {
    setState({ status: "unseen" });
  }, []);

  const value = useMemo<TourContextValue>(() => ({
    status: state.status,
    active,
    currentIndex,
    totalSteps,
    positionInMode,
    mode,
    dontShowAgain: !!state.dontShowAgain,
    start, next, prev, jumpToChapter, setMode, exit, complete, markPrompted, dismiss, reset,
  }), [state, active, currentIndex, totalSteps, positionInMode, mode, start, next, prev, jumpToChapter, setMode, exit, complete, markPrompted, dismiss, reset]);

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useAccountingTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useAccountingTour must be used within AccountingTourProvider");
  return ctx;
}

/** Alias — the tour is no longer accounting-only. New code should use this. */
export const useKoredorTour = useAccountingTour;
export type { TourRole, TourViewMode };

// Re-export for callers that want the underlying total regardless of mode.
export { TOTAL_STEPS };
